import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Upload, Play, CheckCircle, XCircle, AlertCircle, Loader2, FileImage, Video } from 'lucide-react'
import { apiService } from '@/services/api'

interface TestCase {
  input: string
  expected: 'pass' | 'fail'
  description: string
}

interface AssetProperties {
  fileName: string
  fileSize: number
  fileSizeKB: number
  format: string
  dimensions?: { width: number; height: number }
  duration?: number
  mimeType: string
}

interface ValidationResult {
  field: string
  rule: string
  actual: string
  expected: 'pass' | 'fail'
  passed: boolean
  details: string
}

interface AssetTestRunnerProps {
  ruleId: number | string
  rule: string
  field: string
  severity: string
  testCases: TestCase[]
}

export default function AssetTestRunner({ ruleId, rule, field, severity, testCases }: AssetTestRunnerProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [assetProps, setAssetProps] = useState<AssetProperties | null>(null)
  const [validationResults, setValidationResults] = useState<ValidationResult[]>([])
  const [analyzing, setAnalyzing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setSelectedFile(file)
    setError(null)
    setValidationResults([])
    
    // Create preview
    const url = URL.createObjectURL(file)
    setPreviewUrl(url)
    
    // Extract file properties
    const props = await extractFileProperties(file)
    setAssetProps(props)
    
    console.log('📦 Asset Properties:', props)
  }

  const extractFileProperties = async (file: File): Promise<AssetProperties> => {
    const props: AssetProperties = {
      fileName: file.name,
      fileSize: file.size,
      fileSizeKB: Math.round(file.size / 1024),
      format: file.name.split('.').pop()?.toUpperCase() || 'UNKNOWN',
      mimeType: file.type
    }

    // Extract dimensions for images
    if (file.type.startsWith('image/')) {
      const dimensions = await getImageDimensions(file)
      props.dimensions = dimensions
    }

    // Extract duration for videos
    if (file.type.startsWith('video/')) {
      const duration = await getVideoDuration(file)
      props.duration = duration
    }

    return props
  }

  const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
    return new Promise((resolve) => {
      const img = new Image()
      img.onload = () => {
        resolve({ width: img.width, height: img.height })
        URL.revokeObjectURL(img.src)
      }
      img.src = URL.createObjectURL(file)
    })
  }

  const getVideoDuration = (file: File): Promise<number> => {
    return new Promise((resolve) => {
      const video = document.createElement('video')
      video.onloadedmetadata = () => {
        resolve(Math.round(video.duration))
        URL.revokeObjectURL(video.src)
      }
      video.src = URL.createObjectURL(file)
    })
  }

  const runValidation = async () => {
    if (!assetProps) {
      setError('Please select a file first')
      return
    }

    setAnalyzing(true)
    setError(null)
    
    try {
      // Run validation based on field type
      const result = await validateAsset(assetProps, field, rule, severity)
      setValidationResults([result])
      
      console.log('✅ Validation Result:', result)
    } catch (err) {
      console.error('Validation error:', err)
      setError(err instanceof Error ? err.message : 'Validation failed')
    } finally {
      setAnalyzing(false)
    }
  }

  const validateAsset = async (
    props: AssetProperties, 
    field: string, 
    rule: string,
    severity: string
  ): Promise<ValidationResult> => {
    let actual: string
    let passed: boolean
    let details: string

    switch (field) {
      case 'dimensions':
        if (!props.dimensions) {
          return {
            field,
            rule,
            actual: 'No dimensions available',
            expected: 'fail',
            passed: false,
            details: 'Cannot extract dimensions from this file type'
          }
        }
        
        actual = `${props.dimensions.width}x${props.dimensions.height}`
        const allowedDimensions = ['160x600', '300x250', '728x90', '300x600']
        passed = allowedDimensions.includes(actual)
        details = passed 
          ? `✓ ${actual} is an approved display size` 
          : `✗ ${actual} is not in approved list: ${allowedDimensions.join(', ')}`
        break

      case 'fileSize':
        actual = `${props.fileSizeKB}KB`
        const maxSize = 150 // KB
        passed = props.fileSizeKB <= maxSize
        details = passed
          ? `✓ File size ${actual} is within ${maxSize}KB limit`
          : `✗ File size ${actual} exceeds ${maxSize}KB limit`
        break

      case 'format':
        actual = props.format
        const allowedFormats = ['JPG', 'JPEG', 'PNG', 'GIF', 'MP4', 'MOV']
        passed = allowedFormats.includes(actual.toUpperCase())
        details = passed
          ? `✓ ${actual} is an approved format`
          : `✗ ${actual} is not in approved list: ${allowedFormats.join(', ')}`
        break

      case 'duration':
        if (!props.duration) {
          return {
            field,
            rule,
            actual: 'No duration available',
            expected: 'fail',
            passed: false,
            details: 'Cannot extract duration from this file type'
          }
        }
        
        actual = `${props.duration}s`
        const minDuration = 6
        const maxDuration = 30
        passed = props.duration >= minDuration && props.duration <= maxDuration
        details = passed
          ? `✓ Duration ${actual} is within ${minDuration}-${maxDuration}s range`
          : `✗ Duration ${actual} is outside ${minDuration}-${maxDuration}s range`
        break

      case 'fileName':
      case 'naming':
        actual = props.fileName
        // Check for naming conventions
        const hasValidNaming = /^[a-zA-Z0-9_-]+\.[a-zA-Z0-9]+$/.test(actual)
        passed = hasValidNaming
        details = passed
          ? `✓ Filename "${actual}" follows naming convention`
          : `✗ Filename "${actual}" contains invalid characters`
        break

      default:
        // For complex rules, use AI analysis
        const aiResult = await analyzeWithAI(props, rule)
        return aiResult
    }

    return {
      field,
      rule,
      actual,
      expected: passed ? 'pass' : 'fail',
      passed,
      details
    }
  }

  const analyzeWithAI = async (props: AssetProperties, rule: string): Promise<ValidationResult> => {
    try {
      // Call AI analysis endpoint
      const response = await apiService.request('/api/ai-analysis/validate-asset', {
        method: 'POST',
        body: JSON.stringify({
          assetProperties: props,
          rule,
          previewUrl
        })
      })

      if (response.success && response.data) {
        return {
          field: 'ai-analysis',
          rule,
          actual: response.data.analysis,
          expected: response.data.passed ? 'pass' : 'fail',
          passed: response.data.passed,
          details: response.data.reasoning
        }
      }
    } catch (error) {
      console.error('AI analysis failed:', error)
    }

    return {
      field: 'ai-analysis',
      rule,
      actual: 'AI analysis unavailable',
      expected: 'fail',
      passed: false,
      details: 'AI analysis endpoint not available or failed'
    }
  }

  const isImage = selectedFile?.type.startsWith('image/')
  const isVideo = selectedFile?.type.startsWith('video/')

  return (
    <div className="space-y-4">
      {/* File Upload */}
      <div className="border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-6">
        <div className="text-center">
          <Upload className="w-8 h-8 mx-auto mb-3 text-gray-400" />
          <label className="cursor-pointer">
            <span className="text-sm font-medium text-primary hover:underline">
              Upload test asset
            </span>
            <input
              type="file"
              className="hidden"
              accept="image/*,video/*"
              onChange={handleFileSelect}
            />
          </label>
          <p className="text-xs text-muted-foreground mt-1">
            Upload an asset to test this rule
          </p>
        </div>
      </div>

      {/* Asset Preview & Properties */}
      {selectedFile && assetProps && (
        <div className="border rounded-lg p-4 bg-muted/30">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Preview */}
            <div>
              <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                {isImage && <FileImage className="w-4 h-4" />}
                {isVideo && <Video className="w-4 h-4" />}
                Asset Preview
              </h4>
              <div className="bg-black/5 dark:bg-white/5 rounded-lg p-2 flex items-center justify-center min-h-[200px]">
                {isImage && previewUrl && (
                  <img 
                    src={previewUrl} 
                    alt="Preview" 
                    className="max-w-full max-h-[200px] object-contain"
                  />
                )}
                {isVideo && previewUrl && (
                  <video 
                    src={previewUrl} 
                    controls 
                    className="max-w-full max-h-[200px]"
                  />
                )}
              </div>
            </div>

            {/* Properties */}
            <div>
              <h4 className="text-sm font-semibold mb-2">Extracted Properties</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">File:</span>
                  <span className="font-mono text-xs">{assetProps.fileName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Format:</span>
                  <span className="font-semibold">{assetProps.format}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Size:</span>
                  <span className="font-semibold">{assetProps.fileSizeKB}KB</span>
                </div>
                {assetProps.dimensions && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Dimensions:</span>
                    <span className="font-semibold">
                      {assetProps.dimensions.width}×{assetProps.dimensions.height}
                    </span>
                  </div>
                )}
                {assetProps.duration && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Duration:</span>
                    <span className="font-semibold">{assetProps.duration}s</span>
                  </div>
                )}
              </div>

              {/* Run Test Button */}
              <Button
                onClick={runValidation}
                disabled={analyzing}
                className="w-full mt-4"
                size="sm"
              >
                {analyzing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    Test Against Rule
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Validation Results */}
      {validationResults.length > 0 && (
        <div className="space-y-2">
          <h4 className="font-semibold text-sm">Validation Result</h4>
          {validationResults.map((result, index) => (
            <div
              key={index}
              className={`p-4 rounded-lg border ${
                result.passed
                  ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-900'
                  : 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-900'
              }`}
            >
              <div className="flex items-start gap-3">
                {result.passed ? (
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                )}
                
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-sm font-bold ${
                      result.passed ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'
                    }`}>
                      {result.passed ? 'PASS' : 'FAIL'}
                    </span>
                    <code className="text-xs font-mono bg-black/5 dark:bg-white/5 px-2 py-0.5 rounded">
                      {result.actual}
                    </code>
                  </div>
                  
                  <p className="text-sm text-muted-foreground mb-2">
                    {rule}
                  </p>
                  
                  <p className="text-sm">
                    {result.details}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900 rounded-lg">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-600" />
            <span className="text-sm text-red-700 dark:text-red-400">{error}</span>
          </div>
        </div>
      )}
    </div>
  )
}

